import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import { Close } from "@mui/icons-material";
import { addReliefApi } from "../services/appointments.service";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const BOOLEAN_LIST = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

// Add valid enum values for relief fields - MUST match backend schema exactly
const RELIEF_TYPE_OPTIONS = [
  { label: "Discount", value: "Discount" },
  { label: "Waiver", value: "Waiver" },
  { label: "Other", value: "Other" },
];

const RELIEF_UNIT_OPTIONS = [
  { label: "Percentage", value: "Percentage" },
  { label: "Amount", value: "Amount" },
];

const INHOUSE_INVESTIGATION_LABEL = [
  {
    id: 1,
    label: "Category Name",
    value: "categoryName",
  },
  {
    id: 2,
    label: "Subcategory Name",
    value: "subCategoryName",
  },
  {
    id: 3,
    label: "Relief By",
    value: "reliefBy",
  },
  {
    id: 4,
    label: "Relief Type",
    value: "reliefType",
  },
  {
    id: 5,
    label: "Percentage",
    value: "percentage",
  },
  {
    id: 6,
    label: "Cash",
    value: "cash",
  },
  {
    id: 7,
    label: "Qty",
    value: "qty",
  },
  {
    id: 8,
    label: "Amount",
    value: "amount",
  },
];

const EXCLUDE_CATEGORY_LABEL = [
  {
    id: 1,
    label: "Category Name",
    value: "categoryName",
  },
  {
    id: 2,
    label: "Subcategory Name",
    value: "subCategoryName",
  },
  {
    id: 3,
    label: "Action",
    value: "action",
  },
];

const INCLUDE_UNIT_LABEL = [
  {
    id: 1,
    label: "Unit Name",
    value: "departmentName",
  },
  {
    id: 2,
    label: "Relief By",
    value: "reliefBy",
  },
  {
    id: 3,
    label: "Relief Type",
    value: "reliefType",
  },
  {
    id: 4,
    label: "Percentage",
    value: "percentage",
  },
  {
    id: 5,
    label: "Cash",
    value: "cash",
  },
  {
    id: 6,
    label: "Qty",
    value: "qty",
  },
  {
    id: 7,
    label: "Amount",
    value: "amount",
  },
];

const EXCLUDE_UNIT_LABEL = [
  {
    id: 1,
    label: "Department Name",
    value: "departmentName",
  },
  {
    id: 2,
    label: "Action",
    value: "action",
  },
  {
    id: 3,
    label: "",
    value: "",
  },
];

const INCLUDE_ITEM_PACKAGE_LABEL = [
  {
    id: 1,
    label: "Item",
    value: "item",
  },
  {
    id: 2,
    label: "Code",
    value: "code",
  },
  {
    id: 3,
    label: "Relief By",
    value: "reliefBy",
  },
  {
    id: 4,
    label: "Relief",
    value: "relief",
  },
  {
    id: 6,
    label: "Quantity",
    value: "quantity",
  },
  {
    id: 7,
    label: "Action",
    value: "action",
  },
  {
    id: 8,
    label: "",
    value: "",
  },
];

const EXCLUDE_ITEM_PACKAGE_LABEL = [
  {
    id: 1,
    label: "Item",
    value: "item",
  },
  {
    id: 2,
    label: "Code",
    value: "code",
  },
  {
    id: 3,
    label: "Action",
    value: "action",
  },
];

export default function AddRelief() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const initialState = {
    startDate: dayjs(),
    applyToFuture: true,
    applicableToServices: true,
    reliefOnMultipleSurgery: true,
    includedCategories: [{}],
    excludedCategories: [{}],
    includedDepartments: [{}],
    excludedDepartments: [],
    includedItemsOrPackages: [],
    excludedItemsOrPackages: [{}],
  };

  const [formData, setFormData] = useState(initialState);
  const [excludedDepartments, setExcludedDepartments] = useState([]);
  const [includedItemsOrPackages, setIncludedItemsOrPackages] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayInputChange = (e, arrayName) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [arrayName]: [{ ...(prev[arrayName]?.[0] || {}), [name]: value }],
    }));
  };

  // Function to update input values in the state
  const handleInputChange = (event, rowIndex, currentState) => {
    const { value, name } = event.target;

    // Update state without modifying the original array
    currentState((prevState) =>
      prevState.map((row, i) =>
        i === rowIndex ? { ...row, [name]: value } : row
      )
    );
  };
  // handle add item
  const handleAddTableRow = (currentElement, currentState, newRowData) => {
    currentState([...currentElement, newRowData]);
  };

  // handle remove item
  const handleRemoveTableRow = (rowIndex, currentElement, currentState) => {
    currentState(currentElement?.filter((_, index) => index !== rowIndex));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.reliefType || !formData.reliefUnit) {
      toast.error("Please select valid Relief Type and Relief Unit");
      return;
    }
    
    // Prepare the API payload
    const apiPayload = {
      ...formData,
      applicableOnPrice: formData?.applicableOnPrice?.value,
      excludedDepartments,
      includedItemsOrPackages,
    };
    
    try {
      const response = await addReliefApi(apiPayload);
      
      if (response?.code === 200) {
        toast.success(response?.message || "Relief added successfully");
        setFormData(initialState);
        
        // Navigate back to billing page after successful save
        navigate(`/billing/${location.state?.patientId || ''}`);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      console.error("Failed to create relief:", error);
      toast.error("Failed to create relief. Please try again.");
    }
  };

  const renderTableContainer = (title, headLabel, arrayName) => {
    return (
      <Box
        sx={{
          mb: "30px",
          borderRadius: "12px",
          border: "1px solid #CCD4DA",
          p: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography>{title}</Typography>
        </Box>
        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                bgcolor: "#EAEAEA",
              }}
            >
              <TableRow>
                {headLabel?.map((option) => (
                  <TableCell key={option?.id}>
                    <Typography noWrap>{option?.label}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {headLabel?.map((option, columnIndex) => (
                  <TableCell key={columnIndex}>
                    <TextField
                      label={option?.label}
                      name={option?.value}
                      fullWidth
                      value={formData?.[arrayName]?.[0]?.[option?.value] || ""}
                      onChange={(e) => handleArrayInputChange(e, arrayName)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box
          component="form"
          sx={{ width: "100%", pr: "90px", py: "20px" }}
          onSubmit={handleSave}
        >
          {/* add relief details */}
                     <Box
             aria-describedby="popover"
             sx={{
               display: "flex",
               justifyContent: "space-between",
               alignItems: "center",
               mb: "20px",
             }}
           >
             <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
               Add Relief Details
             </Typography>
             <IconButton onClick={() => navigate(`/billing/${location.state?.patientId || ''}`)}>
               <CloseIcon />
             </IconButton>
           </Box>
          {/* first box */}
          <Box
            sx={{
              backgroundColor: "rgba(240, 247, 252, 1)",
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Grid container spacing={2}>
                             {/* relief type */}
               <Grid item xs={12} sm={6} md={4}>
                 <Autocomplete
                   options={RELIEF_TYPE_OPTIONS}
                   getOptionLabel={(option) => option?.label}
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       name="reliefType"
                       label="Relief Type"
                       required
                       fullWidth
                       placeholder="Select relief type"
                     />
                   )}
                   value={RELIEF_TYPE_OPTIONS.find(option => option.value === formData?.reliefType) || null}
                   onChange={(_, newValue) =>
                     handleChange({
                       target: { name: "reliefType", value: newValue?.value || "" },
                     })
                   }
                   sx={{ bgcolor: "white" }}
                 />
               </Grid>

              {/* relief name and view button */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="reliefName"
                  label="Relief Name"
                  required
                  fullWidth
                  value={formData?.reliefName || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* authorized person */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="authorizedPerson"
                  label="Authorized Person"
                  required
                  fullWidth
                  value={formData?.authorizedPerson || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* tpa name */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="tpaName"
                  label="TPA Name"
                  required
                  fullWidth
                  value={formData?.tpaName || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* start and end date */}
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={
                        formData?.startDate ? dayjs(formData?.startDate) : null
                      }
                      onChange={(newValue) =>
                        handleChange({
                          target: { name: "startDate", value: newValue },
                        })
                      }
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      value={
                        formData?.endDate ? dayjs(formData?.endDate) : null
                      }
                      onChange={(newValue) =>
                        handleChange({
                          target: { name: "endDate", value: newValue },
                        })
                      }
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* policy no/card no */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="policyNoOrCardNo"
                  label="Policy No./Card No."
                  required
                  fullWidth
                  value={formData?.policyNoOrCardNo || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* checkbox apply to future*/}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="applyToFuture"
                      checked={formData?.applyToFuture || false}
                      onChange={handleChange}
                    />
                  }
                  label="Do you want to apply on all current and future visit"
                />
              </Grid>
            </Grid>
          </Box>
          {/* second box */}
          <Box
            sx={{
              backgroundColor: "rgba(240, 247, 252, 1)",
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Grid container spacing={2}>
              {/* relief unit and percentage */}
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                                     <Grid item xs={12} sm={6}>
                     <Autocomplete
                       options={RELIEF_UNIT_OPTIONS}
                       getOptionLabel={(option) => option?.label}
                       renderInput={(params) => (
                         <TextField
                           {...params}
                           name="reliefUnit"
                           label="Relief Unit"
                           required
                           fullWidth
                           placeholder="Select relief unit"
                         />
                       )}
                       value={RELIEF_UNIT_OPTIONS.find(option => option.value === formData?.reliefUnit) || null}
                       onChange={(_, newValue) =>
                         handleChange({
                           target: { name: "reliefUnit", value: newValue?.value || "" },
                         })
                       }
                       sx={{ bgcolor: "white" }}
                     />
                   </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="percentage"
                      label="Percentage"
                      type="number"
                      required
                      fullWidth
                      value={formData?.percentage || ""}
                      onChange={handleChange}
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* applicable on price */}
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={BOOLEAN_LIST || []}
                  getOptionLabel={(option) => option?.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="applicableOnPrice"
                      label="Relief Applicable On Price"
                      required
                      fullWidth
                    />
                  )}
                  value={formData?.applicableOnPrice}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: { name: "applicableOnPrice", value: newValue },
                    })
                  }
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* checkbox is relief applicable to services*/}
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="applicableToServices"
                      checked={formData?.applicableToServices || false}
                      onChange={handleChange}
                    />
                  }
                  label="Is Relief Applicable To Services"
                />
              </Grid>

              {/* maximum amount per item */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="maxAmountPerItem"
                  label="Maximum Amount Per Item"
                  type="number"
                  required
                  fullWidth
                  value={formData?.maxAmountPerItem || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* maximum total amount */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="maxTotalAmount"
                  label="Maximum Total Amount"
                  type="number"
                  required
                  fullWidth
                  value={formData?.maxTotalAmount || ""}
                  onChange={handleChange}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          {/* inhouse investigation */}
          {renderTableContainer(
            "Inhouse Investigation",
            INHOUSE_INVESTIGATION_LABEL,
            "includedCategories"
          )}
          {/* exclude category/subcategory */}
          {renderTableContainer(
            "Exclude Category/Subcategory",
            EXCLUDE_CATEGORY_LABEL,
            "excludedCategories"
          )}
          {/* include unit */}
          {renderTableContainer(
            "Include Unit",
            INCLUDE_UNIT_LABEL,
            "includedDepartments"
          )}

          {/* exclude Unit */}
          <Box
            sx={{
              border: `1px solid #CCD4DA`,
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "20px",
              }}
            >
              <Typography>Exclude Unit</Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead
                  sx={{
                    bgcolor: "#EAEAEA",
                  }}
                >
                  <TableRow>
                    {EXCLUDE_UNIT_LABEL?.map((option) => (
                      <TableCell
                        key={option?.id}
                        sx={{
                          textAlign: option?.align || "left",
                          color: "#5D6A82",
                        }}
                      >
                        {option?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {excludedDepartments.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {EXCLUDE_UNIT_LABEL?.map((column, columnIndex) => (
                        <TableCell
                          key={`${rowIndex}${columnIndex}`}
                          sx={{ color: "#5D6A82" }}
                        >
                          {columnIndex === EXCLUDE_UNIT_LABEL?.length - 1 ? (
                            <Close
                              sx={{ cursor: "pointer", fontSize: "18px" }}
                              onClick={() =>
                                handleRemoveTableRow(
                                  rowIndex,
                                  excludedDepartments,
                                  setExcludedDepartments
                                )
                              }
                            />
                          ) : (
                            <TextField
                              label={column?.label}
                              required
                              name={column?.value}
                              value={row[column?.value] || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  e,
                                  rowIndex,
                                  setExcludedDepartments
                                )
                              }
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={EXCLUDE_UNIT_LABEL?.length}
                      sx={{ color: "#92A6CC", cursor: "pointer" }}
                      onClick={() =>
                        handleAddTableRow(
                          excludedDepartments,
                          setExcludedDepartments,
                          {
                            departmentName: "",
                            action: "",
                          }
                        )
                      }
                    >
                      + Add item
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* include items/ package */}
          <Box
            sx={{
              border: `1px solid #CCD4DA`,
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "20px",
              }}
            >
              <Typography>Include Items/Package</Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead
                  sx={{
                    bgcolor: "#EAEAEA",
                  }}
                >
                  <TableRow>
                    {INCLUDE_ITEM_PACKAGE_LABEL?.map((option) => (
                      <TableCell
                        key={option?.id}
                        sx={{
                          textAlign: option?.align || "left",
                          color: "#5D6A82",
                        }}
                      >
                        {option?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {includedItemsOrPackages.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {INCLUDE_ITEM_PACKAGE_LABEL?.map(
                        (column, columnIndex) => (
                          <TableCell
                            key={`${rowIndex}${columnIndex}`}
                            sx={{ color: "#5D6A82" }}
                          >
                            {columnIndex ===
                            INCLUDE_ITEM_PACKAGE_LABEL?.length - 1 ? (
                              <Close
                                sx={{ cursor: "pointer", fontSize: "18px" }}
                                onClick={() =>
                                  handleRemoveTableRow(
                                    rowIndex,
                                    includedItemsOrPackages,
                                    setIncludedItemsOrPackages
                                  )
                                }
                              />
                            ) : (
                              <TextField
                                label={column?.label}
                                required
                                name={column?.value}
                                value={row[column?.value] || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    e,
                                    rowIndex,
                                    setIncludedItemsOrPackages
                                  )
                                }
                              />
                            )}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={INCLUDE_ITEM_PACKAGE_LABEL?.length}
                      sx={{ color: "#92A6CC", cursor: "pointer" }}
                      onClick={() =>
                        handleAddTableRow(
                          includedItemsOrPackages,
                          setIncludedItemsOrPackages,
                          {}
                        )
                      }
                    >
                      + Add item
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* exclude items/package */}
          {renderTableContainer(
            "Exclude Items/Package",
            EXCLUDE_ITEM_PACKAGE_LABEL,
            "excludedItemsOrPackages"
          )}

          <Box
            sx={{
              mb: "20px",
              borderRadius: "12px",
              border: "1px solid #CCD4DA",
              bgcolor: "#EAEAEA",
              py: "10px",
              px: "20px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="reliefOnMultipleSurgery"
                  checked={formData?.reliefOnMultipleSurgery || false}
                  onChange={handleChange}
                />
              }
              label="Relief On Multiple Surgery"
            />
          </Box>

          {/* save button */}
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                width: "200px",
                textTransform: "none",
                backgroundColor: "rgba(146, 166, 204, 1)",
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
